import React, { useState } from 'react';
import { Mail, Check, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Password reset link sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-gradient-to-br from-purple-900/40 to-black/60 border border-purple-500/20 rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
          {/* Logo / Brand */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white tracking-tight">HQ Studios</h1>
            <p className="text-gray-400 text-sm mt-1">High Quality Studios</p>
          </div>

          {sent ? (
            <div className="text-center py-6" data-testid="reset-sent-confirmation">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Check Your Email</h2>
              <p className="text-gray-300 mb-4">
                We've sent a password reset link to <span className="font-semibold text-purple-300">{email}</span>
              </p>
              <p className="text-sm text-gray-400 mb-6">
                Click the link in the email to reset your password. The link will expire in 24 hours.
                <br /><span className="text-yellow-400/80 mt-1 inline-block">Check your Spam/Junk folder if you don't see it.</span>
              </p>
              <button
                onClick={() => { setSent(false); setEmail(''); }}
                className="text-purple-400 hover:text-purple-300 text-sm underline"
                data-testid="send-another-link"
              >
                Send to a different email
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Reset Your Password</h2>
              <p className="text-gray-400 text-sm mb-6">
                Enter the email address you signed up with and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="w-full pl-10 pr-4 py-3 bg-black/50 border border-purple-500/20 rounded-lg text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="send-reset-link-btn"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            </>
          )}

          {/* Back to site link */}
          <div className="mt-6 text-center">
            <a
              href="/"
              className="inline-flex items-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors"
              data-testid="back-to-site-link"
            >
              <ArrowLeft size={14} />
              Back to HQ Studios
            </a>
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          270 Meserole Street, Brooklyn, NY
        </p>
      </div>
    </div>
  );
}
